/**
 * Official Simple Moving Average from TradingView Lightweight Charts
 * indicator-examples (not Supercharts Pine `ta.sma`, which is not an npm API).
 *
 * Source:
 * https://github.com/tradingview/lightweight-charts/blob/master/indicator-examples/src/indicators/moving-average/moving-average-calculation.ts
 */
import { LineData, UTCTimestamp, WhitespaceData } from "lightweight-charts";

export type MovingAveragePoint = {
  time: number;
  value?: number;
};

function ensureNumericTime(data: MovingAveragePoint[]): void {
  for (const item of data) {
    if (typeof item.time !== "number") {
      throw new Error('All items must have a numeric "time" property.');
    }
  }
}

/**
 * TradingView Lightweight Charts SMA: average of the last `length` numeric
 * values. Missing points stay as whitespace and are skipped in the window.
 */
function simpleMovingAverage(
  values: (number | undefined)[],
  length: number
): (number | undefined)[] {
  const result: (number | undefined)[] = [];
  let sum = 0;
  const window: number[] = [];
  for (let i = 0; i < values.length; ++i) {
    const v = values[i];
    if (typeof v !== "number") {
      result.push(undefined);
      continue;
    }
    window.push(v);
    sum += v;
    if (window.length > length) {
      const removed = window.shift()!;
      sum -= removed;
    }
    if (window.length === length && window.every((x) => !Number.isNaN(x))) {
      result.push(sum / length);
    } else {
      result.push(undefined);
    }
  }
  return result;
}

export function calculateMovingAverageIndicatorValues(
  data: MovingAveragePoint[],
  options: { length: number; source?: "value" }
): (LineData<UTCTimestamp | number> | WhitespaceData<UTCTimestamp | number>)[] {
  ensureNumericTime(data);
  const source = options.source ?? "value";
  const values = data.map((d) => d[source] as number | undefined);
  const times = data.map((d) => d.time);
  const ma = simpleMovingAverage(values, options.length);

  return times.map((time, i) =>
    typeof ma[i] === "number" ? { time, value: ma[i] as number } : { time }
  );
}
